import { useState } from "react";
import { Link } from "react-router-dom";

import { ArrowRight, BookOpen, Video, Guitar, Award, Ribbon, CheckCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import fachkraftTeamImg from "@/assets/fachkraft-musik-team.jpg";

const pathSteps = [
  {
    id: 1,
    icon: ArrowRight,
    title: "Anmeldung",
    subtitle: "Jederzeit starten",
    description: "Einstieg jederzeit möglich – erhalte sofort Zugang zur Ausbildung",
    color: "from-[#F97316] to-[#FB923C]",
    bgColor: "bg-[#FFF7ED]",
    iconColor: "text-[#F97316]"
  },
  {
    id: 2,
    icon: BookOpen,
    title: "Einführungskurs",
    subtitle: "Grundlagen lernen",
    description: "Starte mit dem Einführungskurs und lerne die wichtigsten musikalischen Grundlagen kennen",
    color: "from-[#06B6D4] to-[#22D3EE]",
    bgColor: "bg-[#ECFEFF]",
    iconColor: "text-[#06B6D4]"
  },
  {
    id: 3,
    icon: Video,
    title: "Flexibel absolvierbar",
    subtitle: "Dein individueller Weg",
    description: "Kombiniere Selbstlernkurse und Live-Webinare nach deinem eigenen Tempo",
    highlights: [
      { icon: Video, label: "6 Selbstlernkurse", sublabel: "Flexibel lernen" },
      { icon: Video, label: "11 Live-Webinare", sublabel: "Termine ansehen", link: "/termine" },
      { icon: Guitar, label: "Gitarre oder Ukulele", sublabel: "Optional" }
    ],
    color: "from-[#EAB308] to-[#FDE047]",
    bgColor: "bg-[#FEFCE8]",
    iconColor: "text-[#EAB308]"
  },
  {
    id: 4,
    icon: Award,
    title: "Premium-Status",
    subtitle: "Alle Inhalte nutzen",
    description: "Ihr erhaltet Zugriff auf alle Kurse und Inhalte der gesamten Online-Akademie",
    color: "from-[#8B5CF6] to-[#A78BFA]",
    bgColor: "bg-[#F5F3FF]",
    iconColor: "text-[#8B5CF6]"
  },
  {
    id: 5,
    icon: Ribbon,
    title: "Zertifikat",
    subtitle: "Dein Abschluss",
    description: "Nach erfolgreicher Teilnahme erhältst du dein Zertifikat des staatlich zugelassenen Fernlehrgangs",
    color: "from-[#10B981] to-[#34D399]",
    bgColor: "bg-[#ECFDF5]",
    iconColor: "text-[#10B981]"
  }
];

export const InteractivePathSection = () => {
  const [activeStep, setActiveStep] = useState(1);
  
  const activeStepData = pathSteps.find(step => step.id === activeStep);
  
  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src={fachkraftTeamImg} 
          alt="Fachkraft Musik Team" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-primary/10" />
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <Badge className="mb-4 bg-accent/10 text-accent hover:bg-accent/20">
            Dein Weg
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold mb-4">
            Dein Weg zur{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
              Fachkraft
            </span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Alle Bausteine deiner Ausbildung im Überblick
          </p>
        </div>

        {/* Interactive Timeline */}
        <div className="max-w-7xl mx-auto">
          {/* Desktop: Horizontal Steps */}
          <div className="hidden lg:flex items-center justify-between gap-2 mb-12">
            {pathSteps.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                {/* Step Card */}
                <button
                  onClick={() => setActiveStep(step.id)}
                  className={`relative flex flex-col items-center gap-3 p-6 rounded-2xl transition-all duration-300 hover:scale-105 border-2 ${
                    activeStep === step.id 
                      ? `${step.bgColor} shadow-xl border-primary` 
                      : 'bg-card/90 backdrop-blur-sm hover:bg-accent/5 border-border'
                  } w-full group`}
                >
                  {/* Icon */}
                  <div className={`p-4 rounded-xl ${activeStep === step.id ? step.bgColor : 'bg-muted'} transition-all duration-300 group-hover:scale-110`}>
                    <step.icon className={`w-6 h-6 ${activeStep === step.id ? step.iconColor : 'text-muted-foreground'}`} />
                  </div>
                  
                  {/* Text */}
                  <div className="text-center">
                    <h3 className={`font-bold text-sm mb-1 transition-colors ${
                      activeStep === step.id ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {step.title}
                    </h3>
                    <p className="text-xs text-muted-foreground">{step.subtitle}</p>
                  </div>
                  
                  {/* Active Indicator */}
                  {activeStep === step.id && (
                    <div className="absolute -bottom-4 left-1/2 -translate-x-1/2">
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${step.color}`} />
                    </div>
                  )}
                </button>
                
                {/* Arrow between steps */}
                {index < pathSteps.length - 1 && (
                  <ArrowRight className="w-6 h-6 text-primary/40 flex-shrink-0 mx-1" />
                )}
              </div>
            ))}
          </div>

          {/* Mobile: Vertical Steps */}
          <div className="lg:hidden space-y-3 mb-8">
            {pathSteps.map((step) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-300 border-2 ${
                  activeStep === step.id 
                    ? `${step.bgColor} shadow-lg border-primary` 
                    : 'bg-card/90 backdrop-blur-sm hover:bg-accent/5 border-border'
                }`}
              >
                <div className={`p-3 rounded-lg ${activeStep === step.id ? step.bgColor : 'bg-muted'}`}>
                  <step.icon className={`w-5 h-5 ${activeStep === step.id ? step.iconColor : 'text-muted-foreground'}`} />
                </div>
                <div className="flex-1 text-left">
                  <h3 className={`font-bold text-sm ${activeStep === step.id ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {step.title}
                  </h3>
                  <p className="text-xs text-muted-foreground">{step.subtitle}</p>
                </div>
                {activeStep === step.id && <CheckCircle className={`w-5 h-5 ${step.iconColor}`} />}
              </button>
            ))}
          </div>

          {/* Active Step Details */}
          {activeStepData && (
            <Card className={`p-8 lg:p-12 ${activeStepData.bgColor} border-none shadow-xl animate-fade-in`}>
              <div className="flex flex-col lg:flex-row items-start gap-8">
                {/* Icon */}
                <div className={`p-6 rounded-2xl bg-gradient-to-br ${activeStepData.color} shadow-lg flex-shrink-0`}>
                  <activeStepData.icon className="w-10 h-10 text-white" />
                </div>
                
                {/* Content */}
                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="text-2xl lg:text-3xl font-bold mb-2">{activeStepData.title}</h3>
                    <p className="text-lg text-muted-foreground">{activeStepData.description}</p>
                  </div>
                  
                  {/* Special Highlights for Step 3 */}
                  {activeStepData.highlights && (
                    <div className="grid sm:grid-cols-3 gap-4 pt-4">
                      {activeStepData.highlights.map((highlight, idx) => (
                        <Card key={idx} className="p-5 bg-card/80 backdrop-blur-sm hover:shadow-lg transition-all duration-300 hover:scale-105">
                          <highlight.icon className="w-6 h-6 text-primary mb-3" />
                          <h4 className="font-bold text-sm mb-1">{highlight.label}</h4>
                          {highlight.link ? (
                            <Button
                              variant="link"
                              className="p-0 h-auto text-xs text-primary hover:text-primary/80"
                              asChild
                            >
                              <a href={highlight.link} target="_blank" rel="noopener noreferrer">
                                {highlight.sublabel} →
                              </a>
                            </Button>
                          ) : (
                            <p className="text-xs text-muted-foreground">{highlight.sublabel}</p>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* CTA */}
        <div className="text-center mt-12 animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <Button 
            size="lg" 
            className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
            asChild
          >
            <Link to="/ausbildung/fachkraft-musik/kaufen">
              Jetzt zur Ausbildung anmelden
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
